import { useEffect, useRef } from 'react';

import { Box } from '@mui/material';

import { GX_TEXT } from '@/constants/colors';

import AddApp from '@/assets/icons/AddApp';
import { PageInformationContext } from '@/context/PageInformationContext';
import { useDashboardEditLockContext } from '@/features/Dashboard/context/DashboardEditLockContext';
import { DrawerStep, useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import Button from '@/shared/ui/primitives/Button';
import Typography from '@/shared/ui/typography/Text';

import { DashboardActionButtons } from '../../components/DashboardActionButtons';
import { DashboardLayout } from '../../components/DashboardLayout';
import type { DashboardStep } from '../../constants/DashboardStep';
import { AddAppButton } from './components/AddAppButton';

const emptySx = {
  mt: 2,
  marginBottom: 1,
  gap: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center'
};
const titleSx = { fontWeight: 'bold', textAlign: 'center' as const };
const subtitleSx = { mb: 1, textAlign: 'center' as const };
const addAppIconSx = { width: '24px', height: '24px' };
const addAppLabelSx = { marginLeft: '8px' };
const gridSx = {
  display: 'grid',
  gap: { xs: 1, md: 3 },
  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
  width: '100%'
};

export const EmptyDashboard = ({
  tabNames = [],
  activeTab = 0,
  onTabChange,
  setStep,
  title
}: {
  tabNames?: string[];
  activeTab?: number;
  onTabChange?: (index: number) => void;
  setStep: (step: DashboardStep) => void;
  title: string;
}) => {
  const { setDrawerStep } = useDrawerSteps();
  const { requestEditLock } = useDashboardEditLockContext();

  const onClick = () => {
    requestEditLock(() => {
      setDrawerStep(DrawerStep.SelectApp);
    });
  };

  const { setMode, mode } = useAppForm();
  const initialModeRef = useRef(mode);

  useEffect(() => {
    setMode('create');
    return () => {
      setMode(initialModeRef.current);
    };
  }, [setMode]);

  return (
    <PageInformationContext value={{ mainPageTitle: 'Dashboard' }}>
      <DashboardLayout
        activeTab={activeTab}
        renderRight={<DashboardActionButtons setStep={setStep} />}
        tabNames={tabNames}
        title={title}
        onTabChange={onTabChange}
      >
        <Box sx={emptySx}>
          <Typography
            size={24}
            sx={titleSx}
          >
            Start Building Your Dashboard
          </Typography>
          <Typography
            color='gx.subtext'
            size={20}
            sx={subtitleSx}
          >
            Organize your perfect workspace by adding apps
          </Typography>
          <Button
            size='large'
            variant='contained'
            onClick={onClick}
          >
            <AddApp
              fill={GX_TEXT}
              sx={addAppIconSx}
            />
            <Box sx={addAppLabelSx}>Add Your First App</Box>
          </Button>
        </Box>
        <Box sx={gridSx}>
          <AddAppButton onClick={onClick} />
          <AddAppButton onClick={onClick} />
        </Box>
      </DashboardLayout>
    </PageInformationContext>
  );
};
